import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDay, faRotate, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { marketDataApi } from '../api/marketDataApi';

const formatEps = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return Number(value).toFixed(2);
};

const formatSurprise = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const n = Number(value);
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
};

const formatMarketCap = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const n = Number(value);
  if (n >= 1e12) return `${(n / 1e12).toFixed(2)}T`;
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  return n.toLocaleString('en-US');
};

const formatDayLabel = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(`${dateStr}T12:00:00`);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

const EarningsCalendar = ({ onSymbolSelect }) => {
  const [range, setRange] = useState('7');
  const [events, setEvents] = useState([]);
  const [meta, setMeta] = useState({ start: null, end: null, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadEarnings = async () => {
    try {
      setLoading(true);
      setError('');
      const params =
        range === 'today'
          ? { day: new Date().toISOString().slice(0, 10), size: 200 }
          : { days: parseInt(range, 10), size: 200 };

      const response = await marketDataApi.yahoo.getEarningsCalendar(params);
      setEvents(response.data || []);
      setMeta({
        start: response.start,
        end: response.end,
        total: response.total || (response.data || []).length,
      });
    } catch (err) {
      console.error('Error loading earnings calendar:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load earnings');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEarnings();
  }, [range]);

  const grouped = useMemo(() => {
    const map = new Map();
    events.forEach((event) => {
      const key = event.date || 'Unknown';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(event);
    });
    return Array.from(map.entries());
  }, [events]);

  const openSymbol = (symbol) => {
    if (!symbol) return;
    if (onSymbolSelect) onSymbolSelect(symbol);
  };

  return (
    <div className="lt-card overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-hairline">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faCalendarDay} className="text-xs text-muted" />
            <span className="lt-label">Earnings calendar</span>
          </div>
          <div className="text-[11px] text-muted mt-0.5 truncate">
            Yahoo Finance
            {meta.start ? ` · ${meta.start}${meta.end && meta.end !== meta.start ? ` → ${meta.end}` : ''}` : ''}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <select
            value={range}
            onChange={(e) => setRange(e.target.value)}
            className="h-8 px-2 text-[11px] lt-input"
            title="Date range"
          >
            <option value="today">Today</option>
            <option value="7">7 days</option>
            <option value="14">14 days</option>
          </select>
          <button
            type="button"
            onClick={loadEarnings}
            disabled={loading}
            className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-hairline text-muted hover:bg-elevated hover:text-ink disabled:opacity-50"
            title="Refresh"
          >
            <FontAwesomeIcon icon={loading ? faSpinner : faRotate} className={`text-xs ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="mx-3 mt-3 px-2.5 py-2 rounded-md text-xs bg-[var(--lt-bear-dim)] text-bear border border-[rgba(240,113,103,0.28)]">
          {error}
        </div>
      )}

      <div className="max-h-[420px] overflow-y-auto">
        {loading && events.length === 0 ? (
          <div className="px-3 py-8 text-center text-xs text-muted">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
            Loading earnings...
          </div>
        ) : events.length === 0 ? (
          <div className="px-3 py-8 text-center text-xs text-muted">No earnings in this range</div>
        ) : (
          <div className="divide-y divide-hairline">
            {grouped.map(([day, dayEvents]) => (
              <div key={day}>
                <div className="sticky top-0 z-[1] px-3 py-1.5 bg-canvas border-b border-hairline flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-ink">{formatDayLabel(day)}</span>
                  <span className="text-[10px] uppercase tracking-wider text-muted">{dayEvents.length}</span>
                </div>
                <div className="divide-y divide-hairline">
                  {dayEvents.map((event) => {
                    const surprise = event.surprisePercent;
                    const surpriseClass =
                      surprise == null
                        ? 'text-muted'
                        : surprise >= 0
                          ? 'text-bull'
                          : 'text-bear';
                    return (
                      <button
                        key={`${event.symbol}-${event.startDateTime}-${event.eventName || 'earn'}`}
                        type="button"
                        onClick={() => openSymbol(event.symbol)}
                        className="w-full px-3 py-2.5 text-left hover:bg-elevated transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="num text-sm font-semibold text-ink">{event.symbol}</span>
                              <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider border border-hairline text-muted">
                                {event.timing || '—'}
                              </span>
                            </div>
                            <div className="text-[11px] text-muted truncate mt-0.5">
                              {event.company || event.eventName || 'Earnings'}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-[10px] text-muted">Mkt cap</div>
                            <div className="num text-[11px] text-ink">{formatMarketCap(event.marketCap)}</div>
                          </div>
                        </div>
                        <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
                          <div>
                            <div className="text-muted">Est</div>
                            <div className="num text-ink">{formatEps(event.epsEstimate)}</div>
                          </div>
                          <div>
                            <div className="text-muted">Actual</div>
                            <div className="num text-ink">{formatEps(event.epsActual)}</div>
                          </div>
                          <div>
                            <div className="text-muted">Surprise</div>
                            <div className={`num font-semibold ${surpriseClass}`}>
                              {formatSurprise(event.surprisePercent)}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {events.length > 0 && (
        <div className="px-3 py-2 border-t border-hairline text-[10px] text-muted flex items-center justify-between">
          <span>
            Showing {events.length}
            {meta.total ? ` of ${meta.total}` : ''}
          </span>
          <a
            href="https://finance.yahoo.com/calendar/earnings/?offset=0&size=1000"
            target="_blank"
            rel="noopener noreferrer"
            className="text-action hover:underline"
          >
            Open Yahoo
          </a>
        </div>
      )}
    </div>
  );
};

export default EarningsCalendar;
